<template>
    <div class="e-joystick"
        :class="{ active: isDragging, disabled }"
        :style="joystickStyle"
        role="group"
        aria-label="Joystick"
        :aria-valuemin="0"
        :aria-valuemax="100"
        :aria-valuenow="Math.round(emitIntensity * 100)"
        @contextmenu.prevent
        @dragstart.prevent
        @selectstart.prevent
        @pointerdown="onPointerDown">
        <div class="e-joystick__base" />
        <div class="e-joystick__track" />
        <div class="e-joystick__stick" :style="stickStyle"/>
    </div>
</template>

<script>
export default {
    name: `e-joystick`,
    props: {
        size: { type: Number, default: 86 },
        stickSize: { type: Number, default: 34 },
        minInterval: { type: Number, default: 65 },
        maxInterval: { type: Number, default: 500 },
        deadZone: { type: Number, default: 0.12 },
        emitAxis: {
            type: String,
            default: `all`,
            validator: (value) => [`all`, `up`, `down`, `left`, `right`, `vertical`, `horizontal`].includes(value)
        },
        disabled: { type: Boolean, default: false },
    },
    data() {
        return {
            isDragging: false,
            activePointerId: null,
            positionX: 0,
            positionY: 0,
            emitTimer: null,
        };
    },
    computed: {
        radius() {
            return this.size / 2;
        },
        maxTravel() {
            const base = this.radius - (this.stickSize / 2) - 6;
            return Math.max(1, base);
        },
        joystickStyle() {
            return {
                '--joystick-size': `${this.size}px`,
                '--joystick-stick-size': `${this.stickSize}px`,
            };
        },
        stickStyle() {
            return {
                transform: `translate(-50%, -50%) translate(${this.positionX}px, ${this.positionY}px)`,
                transition: this.isDragging ? `none` : `transform 150ms ease-out`
            };
        },
        normalizedPosition() {
            return {
                x: this.clamp(this.positionX / this.maxTravel, -1, 1),
                y: this.clamp(this.positionY / this.maxTravel, -1, 1),
            };
        },
        upIntensity() {
            return this.clamp(-this.normalizedPosition.y, 0, 1);
        },
        downIntensity() {
            return this.clamp(this.normalizedPosition.y, 0, 1);
        },
        leftIntensity() {
            return this.clamp(-this.normalizedPosition.x, 0, 1);
        },
        rightIntensity() {
            return this.clamp(this.normalizedPosition.x, 0, 1);
        },
        magnitude() {
            return this.clamp(Math.hypot(this.normalizedPosition.x, this.normalizedPosition.y), 0, 1);
        },
        axisIntensity() {
            switch (this.emitAxis) {
                case `up`:
                    return this.upIntensity;
                case `down`:
                    return this.downIntensity;
                case `left`:
                    return this.leftIntensity;
                case `right`:
                    return this.rightIntensity;
                case `vertical`:
                    return Math.abs(this.normalizedPosition.y);
                case `horizontal`:
                    return Math.abs(this.normalizedPosition.x);
                case `all`:
                default:
                    return this.magnitude;
            }
        },
        emitIntensity() {
            const normalized = (this.axisIntensity - this.deadZone) / (1 - this.deadZone);
            return this.clamp(normalized, 0, 1);
        },
    },
    beforeDestroy() {
        this.cleanupInteraction();
    },
    beforeUnmount() {
        this.cleanupInteraction();
    },
    watch: {
        disabled(value) {
            if (value) {
                this.resetJoystick();
            }
        }
    },
    methods: {
        clamp(value, min, max) {
            return Math.min(max, Math.max(min, value));
        },
        onPointerDown(event) {
            if (this.disabled) {
                return;
            }
            if (event.button != null && event.button !== 0) {
                return;
            }
            event.preventDefault();

            this.isDragging = true;
            this.activePointerId = event.pointerId;

            this.$el?.setPointerCapture?.(event.pointerId);
            window.addEventListener(`pointermove`, this.onPointerMove, { passive: false });
            window.addEventListener(`pointerup`, this.onPointerUp);
            window.addEventListener(`pointercancel`, this.onPointerUp);

            this.updateFromClientPosition(event.clientX, event.clientY);
            this.$emit(`stick-hold-start`, this.createPointerPayload(event));
            this.$emit(`start`);
        },
        onPointerMove(event) {
            if (!this.isDragging || event.pointerId !== this.activePointerId) {
                return;
            }
            event.preventDefault();
            this.updateFromClientPosition(event.clientX, event.clientY);
        },
        onPointerUp(event) {
            if (!this.isDragging || event.pointerId !== this.activePointerId) {
                return;
            }
            this.$emit(`stick-hold-end`, this.createPointerPayload(event));
            this.resetJoystick();
            this.$emit(`end`);
        },
        updateFromClientPosition(clientX, clientY) {
            const rect = this.$el.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;
            const rawX = clientX - centerX;
            const rawY = clientY - centerY;

            const distance = Math.hypot(rawX, rawY);
            if (distance > this.maxTravel) {
                const ratio = this.maxTravel / distance;
                this.positionX = rawX * ratio;
                this.positionY = rawY * ratio;
            } else {
                this.positionX = rawX;
                this.positionY = rawY;
            }

            this.$emit(`change`, this.createPayload({
                intensity: this.emitIntensity,
                rawIntensity: this.axisIntensity,
                interval: this.getEmitIntervalFromIntensity(this.emitIntensity || 0)
            }));

            this.syncEmitLoop();
        },
        createPayload(overrides = {}) {
            return {
                x: this.normalizedPosition.x,
                y: this.normalizedPosition.y,
                up: this.upIntensity,
                down: this.downIntensity,
                left: this.leftIntensity,
                right: this.rightIntensity,
                magnitude: this.magnitude,
                axis: this.emitAxis,
                ...overrides
            };
        },
        createPointerPayload(event) {
            return this.createPayload({
                pointerId: event?.pointerId ?? null,
                clientX: event?.clientX ?? null,
                clientY: event?.clientY ?? null
            });
        },
        getEmitIntervalFromIntensity(intensity) {
            const safeIntensity = this.clamp(Number(intensity) || 0, 0, 1);
            return Math.round(this.maxInterval - (this.maxInterval - this.minInterval) * safeIntensity);
        },
        syncEmitLoop() {
            const intensity = this.emitIntensity;
            if (!this.isDragging || intensity <= 0) {
                this.clearEmitTimer();
                return;
            }
            if (!this.emitTimer) {
                this.emitWithCurrentIntensity();
                this.scheduleEmitTick();
            }
        },
        emitWithCurrentIntensity() {
            const liveIntensity = this.emitIntensity;
            if (!this.isDragging || liveIntensity <= 0) {
                return;
            }

            const payload = this.createPayload({
                intensity: liveIntensity,
                rawIntensity: this.axisIntensity,
                interval: this.getEmitIntervalFromIntensity(liveIntensity),
            });

            this.$emit(`emit`, payload);
            this.$emit(`pulse`, payload);
        },
        scheduleEmitTick() {
            const intensity = this.emitIntensity;
            if (!this.isDragging || intensity <= 0) {
                this.clearEmitTimer();
                return;
            }

            const delay = this.getEmitIntervalFromIntensity(intensity);
            this.emitTimer = setTimeout(() => {
                this.emitTimer = null;
                this.emitWithCurrentIntensity();
                this.scheduleEmitTick();
            }, delay);
        },
        clearEmitTimer() {
            if (this.emitTimer) {
                clearTimeout(this.emitTimer);
                this.emitTimer = null;
            }
        },
        resetJoystick() {
            this.cleanupInteraction();
            this.positionX = 0;
            this.positionY = 0;
            this.$emit(`change`, this.createPayload({
                intensity: 0,
                rawIntensity: 0,
                interval: this.maxInterval
            }));
        },
        cleanupInteraction() {
            this.clearEmitTimer();
            this.isDragging = false;
            this.activePointerId = null;
            window.removeEventListener(`pointermove`, this.onPointerMove);
            window.removeEventListener(`pointerup`, this.onPointerUp);
            window.removeEventListener(`pointercancel`, this.onPointerUp);
        }
    },
};
</script>

<style lang="scss" scoped>
.e-joystick {
    --joystick-size: 86px;
    --joystick-stick-size: 34px;

    position: relative;
    width: var(--joystick-size);
    height: var(--joystick-size);
    touch-action: none;
    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;
    pointer-events: auto;

    &.disabled {
        opacity: 0.5;
        pointer-events: none;
    }
}

.e-joystick__base {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.35);
    background: radial-gradient(circle at 35% 30%, rgba(255, 255, 255, 0.24), rgba(0, 0, 0, 0.32));
    box-shadow: inset 0 8px 20px rgba(0, 0, 0, 0.3);
}

.e-joystick__track {
    position: absolute;
    left: 50%;
    top: 12%;
    transform: translateX(-50%);
    width: 6px;
    height: 38%;
    border-radius: 999px;
    background: linear-gradient(180deg, rgba(80, 210, 140, 0.85), rgba(255, 255, 255, 0.15));
    opacity: 0.7;
}

.e-joystick__stick {
    position: absolute;
    left: 50%;
    top: 50%;
    width: var(--joystick-stick-size);
    height: var(--joystick-stick-size);
    border-radius: 50%;
    background: radial-gradient(circle at 28% 24%, #ffffff, #d8d8d8 38%, #7c7c7c 100%);
    box-shadow: 0 5px 12px rgba(0, 0, 0, 0.4);
}
</style>
